import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';
import { clearChatStorage } from '../components/LiveChatWidget';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedSearches, setSavedSearches] = useState([]);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      const token = localStorage.getItem('conti_token');
      if (token) {
        try {
          const userData = await authAPI.getMe();
          setUser(userData);
          setFavorites(userData.favorites || []);
          
          // Fetch saved searches
          try {
            const searches = await userAPI.getSavedSearches();
            setSavedSearches(searches);
          } catch (e) {
            console.log('Could not fetch saved searches');
          }
        } catch (e) {
          // Token invalid, clear it
          localStorage.removeItem('conti_token');
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    localStorage.setItem('conti_token', response.token);
    setUser(response.user);
    setFavorites(response.user.favorites || []);
    return response.user;
  };

  const register = async (name, email, password, phone, preferredArea = '', priceRange = '') => {
    const response = await authAPI.register({ 
      name, 
      email, 
      password, 
      phone,
      preferredArea: preferredArea || undefined,
      priceRange: priceRange || undefined
    });
    localStorage.setItem('conti_token', response.token);
    setUser(response.user);
    setFavorites(response.user.favorites || []);
    return response.user;
  };

  const logout = () => {
    localStorage.removeItem('conti_token');
    setUser(null);
    setFavorites([]);
    setSavedSearches([]);
    clearChatStorage();
  };

  const addFavorite = async (propertyId) => {
    if (!user) return;
    try {
      const response = await userAPI.addFavorite(propertyId);
      setFavorites(response.favorites);
    } catch (e) {
      console.error('Failed to add favorite:', e);
    }
  };

  const removeFavorite = async (propertyId) => {
    if (!user) return;
    try {
      const response = await userAPI.removeFavorite(propertyId);
      setFavorites(response.favorites);
    } catch (e) {
      console.error('Failed to remove favorite:', e);
    }
  };

  const isFavorite = (propertyId) => favorites.includes(propertyId);

  const saveSearch = async (searchParams) => {
    if (!user) return;
    try {
      await userAPI.saveSearch(searchParams);
      const searches = await userAPI.getSavedSearches();
      setSavedSearches(searches);
    } catch (e) {
      console.error('Failed to save search:', e);
    }
  };

  const deleteSearch = async (searchId) => {
    if (!user) return;
    try {
      await userAPI.deleteSearch(searchId);
      setSavedSearches(savedSearches.filter(s => s.id !== searchId));
    } catch (e) {
      console.error('Failed to delete search:', e);
    }
  };

  const refreshSavedSearches = async () => {
    if (!user) return;
    try {
      const searches = await userAPI.getSavedSearches();
      setSavedSearches(searches);
    } catch (e) {
      console.error('Failed to refresh saved searches:', e);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      favorites,
      addFavorite,
      removeFavorite,
      isFavorite,
      savedSearches,
      saveSearch,
      deleteSearch,
      refreshSavedSearches,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
